import React, { useState, useEffect } from "react";
import { Header, Footer, Alert } from "../../components";
import { userRequest } from "../../utils/requestMethods";
import { useStateContext } from '../../contexts/ContextProvider';
import {
  Information,
  Address,
  People,
  Account,
  Document,
} from "../../components";
import { FiBriefcase, FiMapPin, FiUsers, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

const Onboarding = ({ history }) => {
  const { school } = useStateContext();
  const [openTab, setOpenTab] = useState(6);
  const [refresh, setRefresh] = useState("");
  const [userInfo, setUserInfo] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const { data } = await userRequest.get(`schools/${school._id}`);
        setUserInfo(data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchTransactions();
  }, [school, refresh]);

  const stepComplete = {
    6: !!(userInfo.school_name && userInfo.phone_number && userInfo.industry && userInfo.description),
    2: !!(userInfo.address?.registered?.address_line && userInfo.address?.registered?.city),
    3: !!(userInfo.officials?.length > 0),
  };

  const allComplete = Object.values(stepComplete).every(Boolean);
  const completedCount = Object.values(stepComplete).filter(Boolean).length;
  const totalSteps = Object.keys(stepComplete).length;

  const statusHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userRequest.get(`/schools/${school._id}/kyc-review-request`);
      setSuccess("KYB has been submitted for review!");
      setTimeout(() => {
        setSuccess("");
        history.push("/onboarding/status");
      }, 3000);
    } catch (error) {
      console.log(error);
      setError("Something went wrong, try again later!");
      setTimeout(() => setError(""), 3000);
    }
    setLoading(false);
  };

  const tabs = [
    { id: 6, label: "Business Details", icon: <FiBriefcase /> },
    { id: 2, label: "Address", icon: <FiMapPin /> },
    { id: 3, label: "Owners", icon: <FiUsers /> },
  ];

  const tabOrder = [6, 2, 3];
  const advanceTab = () => {
    const nextIndex = tabOrder.indexOf(openTab) + 1;
    if (nextIndex < tabOrder.length) {
      setOpenTab(tabOrder[nextIndex]);
      setRefresh(tabOrder[nextIndex]);
    }
  };

  return (
    <>
      <div className="m-3 mt-4 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg border-2 border-gray-200 dark:border-gray-700 min-h-screen">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <Header category="Compliance" title="Activate your business" />

          <div className="flex flex-col items-end gap-2">
            {/* Progress summary */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{completedCount} of {totalSteps} steps completed</span>
              <div className="flex gap-1">
                {tabs.map(tab => (
                  <div
                    key={tab.id}
                    className={`w-2 h-2 rounded-full ${stepComplete[tab.id] ? 'bg-green-500' : 'bg-gray-200'}`}
                  />
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={statusHandler}
              disabled={!allComplete || loading}
              title={!allComplete ? "Complete all steps before submitting" : ""}
              className={`
                inline-flex items-center justify-center py-2.5 px-6
                border border-transparent text-sm font-medium rounded-full
                text-white transition-all shadow-md
                ${!allComplete || loading
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg focus:ring-4 focus:ring-indigo-200'}
              `}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : "Submit for Approval"}
            </button>

            {!allComplete && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <FiAlertCircle className="shrink-0" />
                Complete all steps to submit
              </p>
            )}
          </div>
        </div>

        {success && <Alert type="success" message={success} />}
        {error && <Alert type="error" message={error} />}

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-100 pb-1">
          {tabs.map((tab) => {
            const done = stepComplete[tab.id];
            const active = openTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setOpenTab(tab.id);
                  setRefresh(tab.id);
                }}
                className={`
                  flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-200
                  ${active
                    ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}
                `}
              >
                <span className={`text-lg ${active ? "text-indigo-600" : "text-gray-400"}`}>
                  {tab.icon}
                </span>
                {tab.label}
                {done
                  ? <FiCheckCircle className="text-green-500 w-4 h-4" />
                  : <span className="w-2 h-2 rounded-full bg-amber-400" />
                }
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          <div className={openTab === 2 ? "block animate-fade-in" : "hidden"}>
            <Address setRefresh={setRefresh} address={userInfo.address} onSaveSuccess={advanceTab} />
          </div>
          <div className={openTab === 3 ? "block animate-fade-in" : "hidden"}>
            <People setRefresh={setRefresh} official={userInfo.officials} services={userInfo.services} onSaveSuccess={advanceTab} />
          </div>
          <div className={openTab === 4 ? "block animate-fade-in" : "hidden"}>
            <Account setRefresh={setRefresh} accountInfo={{ account_name: userInfo.account_name, account_number: userInfo.account_number, bank_code: userInfo.bank_code }} />
          </div>
          <div className={openTab === 5 ? "block animate-fade-in" : "hidden"}>
            <Document />
          </div>
          <div className={openTab === 6 ? "block animate-fade-in" : "hidden"}>
            <Information setRefresh={setRefresh} info={userInfo} onSaveSuccess={advanceTab} />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Onboarding;
